import { isValidEmail } from "./regex";
import { UK_PHONE_REGEX } from "./func";
import { isValidDate } from "./isValidDate";
import { validateNationalInsuranceNumber as validateNINumber } from "./validateNationalInsuranceNumber";

export const isValidUKPhone = (phoneNumber) => {
  return UK_PHONE_REGEX.test(phoneNumber.replace(/\s/g, ""));
};

export const isValidURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const scrollToFirstError = (errors) => {
  if (Object.keys(errors).length === 0) return;

  const firstErrorField = Object.keys(errors)[0];
  let element = null;

  element = document.querySelector(`[name="${firstErrorField}"]`);

  if (!element) {
    element = document.getElementById(firstErrorField);
  }

  if (!element) {
    const errorMessage = document.querySelector(
      `[data-field="${firstErrorField}"]`
    );
    if (errorMessage) {
      element = errorMessage;
    }
  }

  if (element) {
    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - 100;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });

    if (
      element.tagName === "INPUT" ||
      element.tagName === "SELECT" ||
      element.tagName === "TEXTAREA"
    ) {
      setTimeout(() => element.focus(), 500);
    }
  }
};

export const validateRequiredFields = (formData, selected) => {
  const errors = {};

  if (!formData.first_name?.trim()) {
    errors.first_name = "First name is required";
  }

  if (!formData.last_name?.trim()) {
    errors.last_name = "Last name is required";
  }

  if (!formData.email_address?.trim()) {
    errors.email_address = "Email address is required";
  } else if (!isValidEmail(formData.email_address)) {
    errors.email_address = "Please enter a valid email address";
  }

  if (!formData.phone_number?.trim()) {
    errors.phone_number = "Phone number is required";
  } else if (!isValidUKPhone(formData.phone_number)) {
    errors.phone_number =
      "Please enter a valid UK phone number (e.g., 07XXX XXXXXX or 020 XXXX XXXX)";
  }

  if (!formData.job_title?.trim()) {
    errors.job_title = "Job title is required";
  }

  if (!formData.seniority?.trim()) {
    errors.seniority = "Seniority is required";
  }

  if (!formData.city?.trim()) {
    errors.city = "City is required";
  }

  if (!formData.country_of_residence?.trim()) {
    errors.country_of_residence = "Country of residence is required";
  }

  if (selected === "enterprise") {
    if (!formData.company_name?.trim()) {
      errors.company_name = "Company name is required";
    }

    if (!formData.company_website_url?.trim()) {
      errors.company_website_url = "Company website is required";
    } else if (!isValidURL(formData.company_website_url)) {
      errors.company_website_url =
        "Please enter a valid URL (e.g., https://example.com)";
    }

    if (!formData.company_address?.trim()) {
      errors.company_address = "Company address is required";
    }

    if (!formData.company_no?.trim()) {
      errors.company_no = "Company number is required";
    }

    if (!formData.tax_identification_no?.trim()) {
      errors.tax_identification_no = "Tax identification number is required";
    }

    if (formData.is_uk_registered === null) {
      errors.is_uk_registered =
        "Please select if your company is a UK partnership";
    }

    if (
      formData.is_uk_registered &&
      !formData.unique_taxpayer_reference_utr?.trim()
    ) {
      errors.unique_taxpayer_reference_utr =
        "UTR is required for UK registered companies";
    }
  }

  scrollToFirstError(errors);
  return errors;
};

export const validateAccountSetupFields = (
  formData,
  streetAddressRef,
  postalCodeRef
) => {
  const errors = {};
  const streetValue = streetAddressRef.current?.value || formData.streetAddress;
  const postalValue = postalCodeRef.current?.value || formData.postalCode;

  const updatedFormData = {
    ...formData,
    streetAddress: streetValue,
    postalCode: postalValue,
  };

  if (!updatedFormData.accountHolderName?.trim()) {
    errors.accountHolderName = "Account holder name is required";
  }

  if (!updatedFormData.accountNumber?.trim()) {
    errors.accountNumber = "Account number is required";
  }

  if (!updatedFormData.confirmAccountNumber?.trim()) {
    errors.confirmAccountNumber = "Please confirm your account number";
  } else if (
    updatedFormData.accountNumber !== updatedFormData.confirmAccountNumber
  ) {
    errors.confirmAccountNumber = "Account numbers do not match";
  }

  if (!updatedFormData.ifscSwiftCode?.trim()) {
    errors.ifscSwiftCode = "Sort code is required";
  } else if (!/^[A-Z0-9]{4,11}$/i.test(updatedFormData.ifscSwiftCode)) {
    errors.ifscSwiftCode =
      "Enter a valid sort code (4-11 alphanumeric characters)";
  }

  if (!updatedFormData.dateOfBirth?.trim()) {
    errors.dateOfBirth = "Date of birth is required";
  } else {
    const [day, month, yearStr] = updatedFormData.dateOfBirth.split("/");
    const year = Number(yearStr);
    const currentYear = new Date().getFullYear();

    if (year && year < 1900) {
      errors.dateOfBirth = "Please enter a valid year (1900 or later)";
    } else if (year && year > currentYear) {
      errors.dateOfBirth = "Year cannot be in the future";
    } else if (!isValidDate(updatedFormData.dateOfBirth)) {
      errors.dateOfBirth = "Please enter a valid date in DD/MM/YYYY format";
    } else {
      const dob = new Date(year, month - 1, day);
      const today = new Date();

      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      if (age < 18) {
        errors.dateOfBirth = "You must be at least 18 years old";
      }
    }
  }

  if (!updatedFormData.streetAddress?.trim()) {
    errors.streetAddress = "Street address is required";
  }

  if (!updatedFormData.postalCode?.trim()) {
    errors.postalCode = "Postal code is required";
  } else if (!/^[A-Za-z0-9\- ]{3,10}$/.test(updatedFormData.postalCode)) {
    errors.postalCode = "Enter a valid postal code";
  }

  if (updatedFormData.nationalInsuranceNumber?.trim()) {
    const niError = validateNINumber(
      updatedFormData.nationalInsuranceNumber
    );
    if (niError) {
      errors.nationalInsuranceNumber = niError;
    }
  }

  scrollToFirstError(errors);
  return { errors, updatedFormData };
};
